import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../shared/Context/auth-context';

export const CARE_BASE_URL = `${(process.env.REACT_APP_BACKEND_URL || 'http://localhost:5006').replace(/\/$/, '')}/api/care`;

export async function careRequest(path, token, method = 'GET', body, signal) {
  const response = await fetch(`${CARE_BASE_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' })
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    signal
  });
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('The care service returned an unreadable response. Please try again.');
  }
  if (!response.ok) {
    const error = new Error(
      response.status === 404 && !data.message?.includes('patient')
        ? 'The care service is unavailable or this record no longer exists. Please refresh or contact your clinic.'
        : data.message || 'The request could not be completed. Please try again.'
    );
    error.status = response.status;
    throw error;
  }
  return data;
}

export function useCareResource(path) {
  const { token } = useContext(AuthContext);
  const [state, setState] = useState({ data: null, loading: !!path, error: null });
  const request = useRef({ controller: null, revision: 0 });
  const reload = useCallback(async () => {
    request.current.controller?.abort();
    const revision = ++request.current.revision;
    if (!path || !token) {
      setState({ data: null, loading: false, error: null });
      return null;
    }
    const controller = new AbortController();
    request.current.controller = controller;
    setState((previous) => ({ ...previous, loading: true, error: null }));
    try {
      const data = await careRequest(path, token, 'GET', undefined, controller.signal);
      if (revision === request.current.revision) {
        setState({ data, loading: false, error: null });
      }
      return data;
    } catch (error) {
      if (revision === request.current.revision && error.name !== 'AbortError') {
        setState((previous) => ({
          ...previous,
          data: [401, 403, 404].includes(error.status) ? null : previous.data,
          loading: false,
          error: error.message
        }));
      }
      return null;
    }
  }, [path, token]);
  useEffect(() => {
    const activeRequest = request.current;
    setState({ data: null, loading: !!path, error: null });
    reload();
    return () => {
      activeRequest.revision += 1;
      activeRequest.controller?.abort();
    };
  }, [path, reload]);
  useEffect(() => {
    if (!path || (path !== '/inbox' && !path.startsWith('/patients/'))) return;
    const refreshVisible = () => {
      if (document.visibilityState !== 'hidden') reload();
    };
    const interval = window.setInterval(refreshVisible, 45000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [path, reload]);
  return { ...state, reload };
}

export function useCareMutation() {
  const { token } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const busy = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const mutate = useCallback(
    async (path, method, body) => {
      if (busy.current) throw new Error('Please wait for the current request to finish.');
      busy.current = true;
      setSaving(true);
      setError(null);
      try {
        return await careRequest(path, token, method, body);
      } catch (err) {
        if (mounted.current) setError(err.message);
        throw err;
      } finally {
        busy.current = false;
        if (mounted.current) setSaving(false);
      }
    },
    [token]
  );
  const clearError = useCallback(() => setError(null), []);
  return { mutate, saving, error, clearError };
}
